import Link from 'next/link';
import { colors, screen } from 'components/styles/variables';
import Ad from 'components/Ad';
import Navbar from 'components/Navbar';
import { LARGE, MEDIUM, SMALL } from 'lib/DeviceScreen';
import adCss from 'components/styles/CenteredAd';
import { LEADERBOARD } from 'lib/GoogleAds/Sizes';
import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useRouter } from 'next/router';
// assets
import autolistLogoRed from 'public/static/images/autolist-logo-red.png';
import arrowLeft from 'public/static/images/arrow-left.png';
import { inIframe } from 'lib/Utilities';
import classnames from 'classnames';
import { useLog } from './Navbar/Desktop/useNavBarClickLog';

export interface HeaderProps {
  url?: string;
  subNav?: ReactNode;
  includeAds?: boolean;
  isVdp?: boolean;
  showOnScroll?: boolean;
  containerWidth?: HeaderContainerWidth;
}

export type HeaderContainerWidth = 'md' | 'lg';

function getHighlightedNavbarItem(url = ''): string | undefined {
  if (url.includes('news-and-analysis')) {
    return 'newsAndAnalysis';
  }

  if (url === '/') {
    return 'home';
  }

  if (url.includes('guides')) {
    return 'guidesAndReviews';
  }

  return undefined;
}

function Header({
  url,
  subNav,
  includeAds = true,
  isVdp = false,
  containerWidth = 'md',
  showOnScroll = false,
}: HeaderProps): JSX.Element {
  return (
    <header data-testid="header-element">
      {includeAds && <LeaderboardAds />}
      <div className={classnames('al-container', { 'al-container-wide': containerWidth === 'lg' })}>
        {isVdp && <BackToSearch />}
        {!isVdp && <AutolistLogo />}
        <Navbar currentItem={getHighlightedNavbarItem(url)} />
      </div>
      {subNav}
      <style jsx>{`
        header {
          position: relative;
          z-index: 600;
          border-bottom: 1px solid ${colors.G1};
          background-color: white;
        }

        .al-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 63px;
          margin-right: auto;
          margin-left: auto;
          padding-left: 15px;
          padding-right: 15px;
        }

        @media (min-width: ${screen.smMin}) {
          .al-container {
            width: 750px;
          }
        }

        @media (min-width: ${screen.mdMin}) {
          .al-container {
            width: 970px;
          }
        }

        @media (min-width: ${screen.lgMin}) {
          .al-container {
            width: 1170px;
          }

          .al-container-wide {
            width: 1440px;
            padding-left: 24px;
            padding-right: 24px;
          }
        }

        @media (max-width: ${screen.smMin}) {
          header {
            position: ${showOnScroll ? 'fixed' : 'relative'};
            width: 100%;
          }
        }
      `}</style>
    </header>
  );
}

const LeaderboardAds = (): JSX.Element => (
  <div>
    <Ad
      adConfig={{
        dimensions: [[728, 90]],
        targeting: { inLine: 1 },
        screenSizes: [LARGE, MEDIUM],
      }}
      placeholderSize={LEADERBOARD}
      className={`${adCss.className}`}
    />
    <Ad
      adConfig={{
        dimensions: [[728, 90]],
        targeting: { inLine: 1 },
        screenSizes: [SMALL],
      }}
      placeholderSize={LEADERBOARD}
      className={`${adCss.className}`}
    />
    {adCss.styles}
    <style jsx>{`
      div {
        background-color: ${colors.G1};
        padding-bottom: 10px;
        padding-top: 10px;
      }
    `}</style>
  </div>
);

const BackToSearch = (): JSX.Element => {
  const { t } = useTranslation();
  const log = useLog();
  let { back } = useRouter();

  if (inIframe()) {
    back = (): void => {
      // SRP is wrapping VDP in an iframe. To go back, message the SRP with a 'srp' message.
      window.parent.postMessage('srp', '*');
    };
  }

  return (
    <div
      className="back-button-container"
      role="button"
      onClick={(): void => {
        log('back_to_search_tap');
        back();
      }}
    >
      <div data-testid="back-icon" className="back-icon" />
      <a className="back-button">{t('vdp.backToSearch')}</a>
      <style jsx>{`
        .back-button-container {
          display: flex;
          flex-direction: row;
          cursor: pointer;
        }

        .back-icon {
          width: 15px;
          height: 21px;
          background-image: url('${arrowLeft}');
          background-size: 12px 21px;
          background-repeat: no-repeat;
          margin-right: 2px;
        }
      `}</style>
    </div>
  );
};

const AutolistLogo = (): JSX.Element => {
  const log = useLog();

  return (
    <>
      <Link href="/">
        <a
          data-testid="logo"
          onClick={(): void => {
            log('autolist_icon_tap');
          }}
          className="logo"
        />
      </Link>
      <style jsx>{`
        .logo {
          display: inline-block;
          width: 137px;
          height: 20px;
          background-image: url('${autolistLogoRed}');
          background-size: 137px 20px;
          background-repeat: no-repeat;
        }
      `}</style>
    </>
  );
};

export default Header;
